import type {
  FormFieldDefinition,
  FormSubmissionRecord,
} from '@tov-plus/domain';

import { type guestApi } from '../../app/api';
import { getGuestErrorMessage } from '../../lib/errors/guestErrors';

export interface GuestRsvpPageState {
  invitation: Awaited<ReturnType<typeof guestApi.getRsvp>>['invitation'];
  publishableInvitation: Awaited<
    ReturnType<typeof guestApi.getRsvp>
  >['publishableInvitation'];
  unitDisplayName: string;
  participantDisplayNames: string[];
  identityContext: Awaited<
    ReturnType<typeof guestApi.getRsvp>
  >['identityContext'];
  events: Awaited<ReturnType<typeof guestApi.getRsvp>>['events'];
}

export type SubmitRsvpStatus = 'pending' | 'attending' | 'declined';

export type EventDraft = {
  status: SubmitRsvpStatus;
  note: string;
  linkedFormAnswers: Record<
    string,
    string | string[] | boolean | number | null
  >;
};

type RemoteEventDraftMap = Record<string, EventDraft>;

export function getRsvpDraftStorageKey(
  occasionSlug: string,
  guestToken: string,
) {
  return `tov-plus:guest-rsvp-draft:${occasionSlug}:${guestToken}`;
}

export function getParticipantEventDraftKey(
  eventId: string,
  participantActorId: string,
) {
  return `${eventId}:${participantActorId}`;
}

function isEventDraft(value: unknown): value is EventDraft {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return false;
  }

  const candidate = value as Partial<EventDraft>;

  return (
    isSubmitRsvpStatus(candidate.status) &&
    typeof candidate.note === 'string' &&
    typeof candidate.linkedFormAnswers === 'object' &&
    candidate.linkedFormAnswers !== null &&
    !Array.isArray(candidate.linkedFormAnswers)
  );
}

export function isSubmitRsvpStatus(value: unknown): value is SubmitRsvpStatus {
  return value === 'pending' || value === 'attending' || value === 'declined';
}

export function getSubmitRsvpStatus(value: unknown): SubmitRsvpStatus {
  return isSubmitRsvpStatus(value) ? value : 'pending';
}

export function getGuestRsvpSubmissionStatus(
  draft: EventDraft | undefined,
): Exclude<SubmitRsvpStatus, 'pending'> {
  return draft?.status === 'attending' ? 'attending' : 'declined';
}

export function normalizeRemoteEventDrafts(
  value: unknown,
): RemoteEventDraftMap {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return {};
  }

  return Object.fromEntries(
    Object.entries(value).filter(([, candidate]) => {
      if (!isEventDraft(candidate)) {
        return false;
      }

      return isSubmitRsvpStatus(candidate.status);
    }),
  );
}

export function isDeadlinePassed(deadlineAt: string | null) {
  if (!deadlineAt) {
    return false;
  }

  const timestamp = Date.parse(deadlineAt);

  return Number.isFinite(timestamp) && timestamp <= Date.now();
}

export function getRsvpErrorMessage(error: unknown) {
  return getGuestErrorMessage(
    error,
    'The RSVP experience could not be loaded.',
  );
}

export function getInitialFieldValue(
  field: FormFieldDefinition,
  submission: FormSubmissionRecord | null,
): string | string[] | boolean | number | null {
  const answer =
    submission?.answers.find((candidate) => candidate.fieldId === field.id)
      ?.value ?? null;

  if (answer !== null) {
    return answer;
  }

  return field.fieldType === 'multi_select' ? [] : null;
}

export function createEventDrafts(
  events: GuestRsvpPageState['events'],
  identityContext?: GuestRsvpPageState['identityContext'],
): Record<string, EventDraft> {
  return Object.fromEntries(
    events.flatMap((event) => {
      const participantResponses =
        event.participantResponses.length > 0
          ? event.participantResponses
          : identityContext
            ? identityContext.participantActorIds.map(
                (participantActorId, index) => ({
                  participantActorId,
                  participantDisplayName:
                    identityContext.participantDisplayNames[index] ??
                    participantActorId,
                  response:
                    event.response?.participantActorIds.includes(
                      participantActorId,
                    ) === true
                      ? event.response
                      : null,
                }),
              )
            : [
                {
                  participantActorId: event.response?.responseUnitActorId ?? '',
                  participantDisplayName: '',
                  response: event.response,
                },
              ];

      return participantResponses
        .filter((participant) => participant.participantActorId)
        .map((participant) => [
          getParticipantEventDraftKey(
            event.eventId,
            participant.participantActorId,
          ),
          {
            status: getSubmitRsvpStatus(participant.response?.status),
            note: participant.response?.note ?? '',
            linkedFormAnswers: Object.fromEntries(
              (event.linkedForm?.fields ?? []).map((field) => [
                field.id,
                getInitialFieldValue(field, event.linkedFormSubmission),
              ]),
            ),
          },
        ]);
    }),
  );
}
